import type { PathOptions } from "leaflet";

import { BlastPatternStatus } from "../../DTOs/Telemetry/BlastPatternFenceDTO";

export const MATERIAL_CLASS_COLOURS: Record<string, string> = {
  ROM: "#B91C1C",
  ROMC: "#7F1D1D",
  MG: "#D97706",
  MGC: "#92400E",
  POM: "#CA8A04",
  OXIDE: "#DB2777",
  NAF: "#65A30D",
  PAF: "#7C3AED",
};

function materialClassColour(materialClass: string | null | undefined): string | undefined {
  const key = materialClass?.trim().toUpperCase();
  return key && Object.hasOwn(MATERIAL_CLASS_COLOURS, key) ? MATERIAL_CLASS_COLOURS[key] : undefined;
}

export type FenceStyle = { colour: string; fillOpacity: number };

export const DEFAULT_FENCE_STYLE: FenceStyle = { colour: "#4F46E5", fillOpacity: 0.18 };

export const FENCE_STYLES: Record<number, FenceStyle> = {
  1: DEFAULT_FENCE_STYLE,
  2: { colour: "#475569", fillOpacity: 0.3 },
  3: { colour: "#4D7C0F", fillOpacity: 0.3 },
  4: { colour: "#B45309", fillOpacity: 0.55 },
  5: { colour: "#A16207", fillOpacity: 0.55 },
  6: { colour: "#1F2937", fillOpacity: 0.55 },
  7: { colour: "#475569", fillOpacity: 0.3 },
  8: { colour: "#0E7490", fillOpacity: 0.16 },
};

const STOCKPILE_FILL_OPACITY = 0.55;

export function fenceStyle(locationTypeId: number | null | undefined, stockpileType?: string | null): FenceStyle {
  const grade = materialClassColour(stockpileType);
  if (grade) return { colour: grade, fillOpacity: STOCKPILE_FILL_OPACITY };

  if (locationTypeId != null && Object.hasOwn(FENCE_STYLES, locationTypeId))
    return FENCE_STYLES[locationTypeId];

  return DEFAULT_FENCE_STYLE;
}

export function fencePathOptions(locationTypeId: number | null | undefined, stockpileType?: string | null): PathOptions {
  const { colour, fillOpacity } = fenceStyle(locationTypeId, stockpileType);
  return { color: colour, weight: 1.5, opacity: 0.9, fillColor: colour, fillOpacity };
}

export type RoadStyle = { colour: string; weight: number; dash?: string; casing?: string };

export const ROAD_STYLES: Record<string, RoadStyle> = {
  "main-haul": { colour: "#FAFAF9", weight: 5, casing: "#44403C" },
  "pit-ramp": { colour: "#E7E5E4", weight: 4, casing: "#44403C" },
  "dump-ramp": { colour: "#E7E5E4", weight: 4, casing: "#44403C" },
  "bench-access": { colour: "#57534E", weight: 2, dash: "6 4" },
  "dump-crest": { colour: "#57534E", weight: 2.5 },
  "yard": { colour: "#57534E", weight: 2.5 },
  "service": { colour: "#78716C", weight: 2.5 },
  "lv-road": { colour: "#2563EB", weight: 1.5, dash: "5 4" },
};

// Note: an unrecognised class falls back to the main-haul style, so a site's own vocabulary still draws
// something sensible.
export function roadStyle(roadClass: string | null | undefined): RoadStyle {
  if (roadClass && Object.hasOwn(ROAD_STYLES, roadClass))
    return ROAD_STYLES[roadClass];

  return ROAD_STYLES["main-haul"];
}

const ROAD_WEIGHT_REFERENCE_ZOOM = 15;
const MIN_ROAD_WEIGHT = 1;
const MAX_ROAD_WEIGHT = 10;
const MIN_CASING_EDGE = 2;
const CASING_EDGE_RATIO = 0.3;

export function scaledRoadWeight(weight: number, zoom: number): number {
  const scaled = weight * 2 ** ((zoom - ROAD_WEIGHT_REFERENCE_ZOOM) / 2);
  return Math.min(MAX_ROAD_WEIGHT, Math.max(MIN_ROAD_WEIGHT, scaled));
}

// Note: the dash scales with the drawn weight, and a dashed class gets butt caps. A round cap adds the line's
// width to every dash, so a fixed dash under a widening line closes up and reads as a solid road.
export function roadPathOptions(roadClass: string | null | undefined, zoom: number): PathOptions {
  const style = roadStyle(roadClass);
  const weight = scaledRoadWeight(style.weight, zoom);
  const scale = weight / style.weight;

  return {
    color: style.colour,
    weight,
    opacity: 1,
    dashArray: style.dash?.split(" ").map(length => Math.round(Number(length) * scale * 100) / 100).join(" "),
    lineCap: style.dash ? "butt" : "round",
    lineJoin: "round",
  };
}

// Note: the edge is added to the road's drawn weight rather than scaled with it, so a road clamped at its
// widest keeps its edge instead of the two meeting at the same width.
export function roadCasingPathOptions(roadClass: string | null | undefined, zoom: number): PathOptions | null {
  const style = roadStyle(roadClass);
  if (!style.casing) return null;

  const weight = scaledRoadWeight(style.weight, zoom);

  return {
    color: style.casing,
    weight: weight + Math.max(MIN_CASING_EDGE, weight * CASING_EDGE_RATIO),
    opacity: 0.9,
    lineCap: "round",
    lineJoin: "round",
  };
}

export const BLAST_PATTERN_COLOUR = "#B0651F";

const BLAST_PATTERN_STATUSES: readonly BlastPatternStatus[] = ["Designed", "Drilled", "Fired", "Digging"];

export function blastPatternStatus(status: string | null | undefined): BlastPatternStatus {
  const known = BLAST_PATTERN_STATUSES.find(s => s === status);
  return known ?? "Fired";
}

// Note: dashArray is set on every status, even to undefined, because setStyle merges into the existing
// options and a pattern that changes status keeps its drawn shape; a Designed dash would outlive it.
export const BLAST_PATTERN_STATUS_STYLES: Record<BlastPatternStatus, PathOptions> = {
  Designed: { weight: 1, dashArray: "6 4", opacity: 0.8, fillOpacity: 0.04 },
  Drilled: { weight: 2, dashArray: "1 5", opacity: 0.85, fillOpacity: 0.06 },
  Fired: { weight: 1.5, dashArray: undefined, opacity: 0.9, fillOpacity: 0.08 },
  Digging: { weight: 3, dashArray: undefined, opacity: 0.95, fillOpacity: 0.15 },
};

export function blastPatternPathOptions(status: string | null | undefined): PathOptions {
  return { color: BLAST_PATTERN_COLOUR, ...BLAST_PATTERN_STATUS_STYLES[blastPatternStatus(status)] };
}

export function upperFirst(text: string | null | undefined): string {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function blastPatternTooltip(pattern: { reference: string; blastType?: string | null; status?: string | null }): string {
  return [pattern.reference, upperFirst(pattern.blastType), pattern.status ?? ""]
    .filter(part => part !== "")
    .join(" - ");
}

// Note: keyed on blastMasterId alone, because neither fence carries the pit stage the Ore Tally pairs it
// with. An id that more than one live pattern shares is left out rather than guessed.
export function blastPatternTooltipsByMaster(
  patterns: { blastMasterId: number; reference: string; blastType?: string | null; status?: string | null }[],
): Map<number, string> {
  const tooltips = new Map<number, string>();
  const shared = new Set<number>();

  for (const pattern of patterns) {
    if (tooltips.has(pattern.blastMasterId)) shared.add(pattern.blastMasterId);
    else tooltips.set(pattern.blastMasterId, blastPatternTooltip(pattern));
  }

  shared.forEach(id => tooltips.delete(id));
  return tooltips;
}

export const DIG_BLOCK_OTHER_WASTE_COLOUR = "#6B7280";
export const DIG_BLOCK_OTHER_ORE_COLOUR = "#CA8A04";
export const DIG_BLOCK_OUTLINE_COLOUR = "#292524";

export function digBlockColour(materialClass: string | null | undefined, isWaste: boolean): string {
  return materialClassColour(materialClass) ?? (isWaste ? DIG_BLOCK_OTHER_WASTE_COLOUR : DIG_BLOCK_OTHER_ORE_COLOUR);
}

export function digBlockPathOptions(materialClass: string | null | undefined, isWaste: boolean): PathOptions {
  return { color: DIG_BLOCK_OUTLINE_COLOUR, weight: 1, opacity: 0.5, fillColor: digBlockColour(materialClass, isWaste), fillOpacity: 0.6 };
}

export function digBlockTooltip(block: { oreBlock: string; materialClass?: string | null; flitch: number; areaM2?: number | null }): string {
  const parts = [block.oreBlock, block.materialClass ?? "", `Flitch ${block.flitch}`];
  if (block.areaM2 != null) parts.push(`${Math.round(block.areaM2).toLocaleString()} m²`);

  return parts.filter(part => part !== "").join(" - ");
}

export function topLiveFlitchBlocks<T extends { isTopLiveFlitch: boolean }>(blocks: T[]): T[] {
  return blocks.filter(block => block.isTopLiveFlitch === true);
}
